import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import useInventory from "../../../services/Inventory/useInventory";
import useGlobalInventory from "../../../services/Inventory/useGlobalInventory";

const AddItemVariantModal = ({ show, onHide, itemId, itemName, onSuccess }) => {
  const { addItemVariant, getVariantsByItem, deleteVariant, updateVariant } =
    useInventory();

  const {
    fetchAllInventoryVariants,
    variants,
    updateInventoryVariant,
    deleteInventoryVariant,
  } = useGlobalInventory();

  const [form, setForm] = useState({
    variation: "",
    price: "",
    category: "",
  });

  const [editMode, setEditMode] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const VARIANT_CATEGORY_GROUPS = useMemo(
    () => [
      {
        label: "Industry",
        options: [
          "Food",
          "Bakery",
          "Grocery",
          "Dairy",
          "Beverages",
          "Household",
          "Personal Care",
          "Pharmacy",
          "Pet Care",
          "Baby Care",
          "Stationery",
          "Home & Kitchen",
          "Cleaning",
          "Electronics",
          "Fashion",
          "Health & Wellness",
        ],
      },
      {
        label: "Food Types",
        options: [
          "Fruits & Vegetables",
          "Staples & Grains",
          "Rice & Flour",
          "Pulses & Lentils",
          "Oils & Ghee",
          "Masala & Spices",
          "Snacks & Namkeen",
          "Biscuits & Cookies",
          "Chocolates & Sweets",
          "Breakfast & Cereals",
          "Instant & Ready-to-Eat",
          "Bakery & Breads",
          "Cakes & Pastries",
          "Dairy & Cheese",
          "Paneer & Tofu",
          "Eggs & Meat",
          "Seafood",
          "Frozen Foods",
          "Beverages - Soft Drinks",
          "Beverages - Juices",
          "Tea & Coffee",
          "Water & Ice",
        ],
      },
    ],
    [],
  );

  const [loading, setLoading] = useState(false);

  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
const [categorySearch, setCategorySearch] = useState("");

  useEffect(() => {
    if (show) {
      setForm({
        variation: "",
        price: "",
        category: "",
      });

      fetchAllInventoryVariants();
    }
  }, [show]);

  const handleAdd = async () => {
    if (!form.variation.trim()) {
      toast.error("Variant name required");
      return;
    }

    try {
      setLoading(true);

      const success = await addItemVariant({
        itemId,
        variation: form.variation,
        price: form.price,
        category: form.category,
      });

      if (success) {
        toast.success("Variant added");
        setForm({ variation: "", price: "", category: "" });

        fetchAllInventoryVariants();
        onSuccess && onSuccess();
      }
    } catch {
      toast.error("Error adding variant");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedVariant) return;

    if (!form.variation.trim()) {
      toast.error("Variant name required");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        variantId: selectedVariant.pk_variant_id,
        variant_title: form.variation,
        category: form.category,
      };

      const success = await updateInventoryVariant(payload);

      if (success) {
        toast.success("Variant updated");

        // reset UI
        setEditMode(false);
        setSelectedVariant(null);
        setForm({
          variation: "",
          price: "",
          category: "",
        });

        fetchAllInventoryVariants();
        onSuccess && onSuccess();
      }
    } catch (err) {
      console.error(err);
      // toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (variantId) => {
    if (!variantId) return;

    try {
      const res = await deleteInventoryVariant(String(variantId));

      if (res?.responseCode === "00") {
        toast.success("Variant deleted");

        fetchAllInventoryVariants();
        onSuccess && onSuccess();
      } else {
        toast.error(res?.responseMessage || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Delete failed");
    }
  };

  if (!show) return null;

  const filteredGroups = VARIANT_CATEGORY_GROUPS.map((group) => ({
  ...group,
  options: group.options.filter((opt) =>
    opt.toLowerCase().includes(categorySearch.toLowerCase())
  ),
})).filter((group) => group.options.length > 0);

  return (
    <>

    <div className="fixed inset-0 z-50 flex items-center justify-center montserrat-font-1">
      {/* BACKDROP */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm montserrat-font-1"
        onClick={onHide}
      />

      {/* MODAL */}
      <div className="relative bg-white w-full max-w-3xl rounded-xl shadow-lg z-10 montserrat-font-1">
        {/* HEADER */}
        <div className="flex justify-between items-center px-5 py-3 border-b">
          <h2 className="font-semibold text-lg">Variant Manager</h2>
          <button onClick={onHide}>✕</button>
        </div>

        {/* BODY */}
        <div className="p-5 space-y-4 montserrat-font-1">
          {/* TOP FORM */}
          <div className="grid grid-cols-3 gap-4">
            {/* VARIANT TITLE */}
            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-600 mb-1">
                Variant Title
              </label>
              <input
                type="text"
                placeholder="e.g. Regular, Large, Sugar Free"
                value={form.variation}
                onChange={(e) =>
                  setForm({ ...form, variation: e.target.value })
                }
                className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* CATEGORY */}
            <div className="flex flex-col relative">
  <label className="text-xs font-semibold text-gray-600 mb-1">
    Category (for variant)
  </label>

  <div
    onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
    className="border rounded-md px-3 py-2 text-sm bg-white cursor-pointer flex justify-between items-center"
  >
    <span>{form.category || "Select Category"}</span>
    <span>▼</span>
  </div>

  {showCategoryDropdown && (
    <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50">
      <input
        type="text"
        placeholder="Search category..."
        value={categorySearch}
        onChange={(e) => setCategorySearch(e.target.value)}
        className="w-full border-b px-3 py-2 outline-none text-sm"
      />

      <div className="max-h-60 overflow-y-auto">
        {filteredGroups.map((group) => (
          <div key={group.label}>
            <div className="sticky top-0 bg-gray-100 px-3 py-2 text-xs font-bold text-gray-600">
              {group.label}
            </div>

            {group.options.map((opt) => (
              <div
                key={opt}
                onClick={() => {
                  setForm({ ...form, category: opt });
                  setShowCategoryDropdown(false);
                  setCategorySearch("");
                }}
                className="px-3 py-2 cursor-pointer hover:bg-green-50 text-sm"
              >
                {opt}
              </div>
            ))}
          </div>
        ))}

        {filteredGroups.length === 0 && (
          <div className="px-3 py-3 text-gray-500 text-sm">
            No category found
          </div>
        )}
      </div>
    </div>
  )}
</div>

           <div className="flex justify-end gap-2 mt-5">
  {editMode ? (
    <>
      <button
        onClick={handleUpdate}
        disabled={loading}
        className="bg-blue-600 text-white px-4 py-2 text-xs rounded-md hover:bg-blue-700 h-8"
      >
        {loading ? "Updating..." : "Update Variant"}
      </button>

      <button
        onClick={() => {
          setEditMode(false);
          setSelectedVariant(null);
          setForm({ variation: "", price: "", category: "" });
        }}
        className="px-3 py-1 text-sm border rounded-md text-gray-600 h-8"
      >
        Cancel
      </button>
    </>
  ) : (
    <button
      onClick={handleAdd}
      disabled={loading}
      className="bg-green-600 text-white px-4 py-2 text-sm rounded-md hover:bg-green-700 h-8"
    >
      {loading ? "Adding..." : "Add Variant"}
    </button>
  )}
</div>
          </div>

          {/* TABLE */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-4 py-2 font-medium text-sm">
              Existing Variants
            </div>

            <div className="max-h-60 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-2">Title</th>
                    <th className="px-4 py-2">Category</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {variants
                    ?.filter((v) => String(v.itemId) === String(itemId))
                    .map((v) => (
                      <tr key={v.id} className="border-t">
                        <td className="px-4 py-2">{v.variant_title}</td>
                        <td className="px-4 py-2 text-gray-500">
                          {v.category || "-"}
                        </td>

                        <td className="px-4 py-2 text-right space-x-2">
                          <button
                            onClick={() => {
                              setEditMode(true);
                              setSelectedVariant(v);

                              setForm({
                                variation: v.variant_title || "",
                                price: v.price || "",
                                category: v.category || "",
                              });
                            }}
                            className="text-blue-600 text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              setDeleteId(v.pk_variant_id);
                              setDeleteModal(true);
                            }}
                            className="text-red-600 text-sm"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex justify-between items-center px-5 py-3 border-t montserrat-font-1">
          <button
            onClick={onHide}
            className="px-4 py-2 border rounded-md text-gray-600"
          >
            Close
          </button>

          <button
            onClick={onHide}
            className="bg-green-600 text-white px-4 py-2 rounded-md"
          >
            Done
          </button>
        </div>
      </div>

      {/* delete model */}

      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center montserrat-font-1">
          {/* BACKDROP */}
          <div className="absolute inset-0 bg-black/40" />

          {/* MODAL */}
          <div className="relative bg-white rounded-xl shadow-lg w-full max-w-sm p-6 z-10">
            <h3 className="text-lg font-semibold mb-2 text-gray-800">
              Delete Variant
            </h3>

            <p className="text-sm text-gray-600 mb-5 montserrat-font-2">
              Are you sure you want to delete this variant?
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteModal(false);
                  setDeleteId(null);
                }}
                className="px-4 py-2 border rounded-md text-gray-600"
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  handleDelete(deleteId);
                  setDeleteModal(false);
                  setDeleteId(null);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default AddItemVariantModal;
